import * as React from 'react';
import type { SyntheticEvent } from 'react';

export type ErrorMessage = string;
export type Errors = Record<string, ErrorMessage>;

export type Validation = {
  validator: (value: string) => boolean;
  errorMessage: string;
};
export type Validations = Map<string, Array<Validation> | Validation>;

export type InputValidationAction = {
  type: 'INPUT_VALIDATION_COMPLETED';
  errorMessage: string | null;
  inputName: string;
};

export type FormValidationAction = {
  type: 'FORM_VALIDATION_COMPLETED';
  errors: Errors;
};

function runValidation(
  inputName: string,
  validations: Validations
): string | null {
  let inputValidations = validations.get(inputName);
  if (!inputValidations) return null;

  const input = document.querySelector(`[name='${inputName.toString()}']`);
  if (
    !(
      input instanceof HTMLInputElement ||
      input instanceof HTMLSelectElement ||
      input instanceof HTMLTextAreaElement
    )
  ) {
    return null;
  }

  inputValidations = Array.isArray(inputValidations)
    ? inputValidations
    : [inputValidations];

  for (const validation of inputValidations) {
    if (!validation.validator(input.value)) {
      return validation.errorMessage;
    }
  }
  return null;
}

function runValidations(validations: Validations): Errors {
  const errors: Record<string, string> = {};
  validations.forEach((_, inputName) => {
    const error = runValidation(inputName, validations);
    if (error) {
      errors[inputName] = error;
    }
  });
  return errors;
}

type ValidationAction = InputValidationAction | FormValidationAction;

function errorReducer(state: Errors, action: ValidationAction): Errors {
  switch (action.type) {
    case 'INPUT_VALIDATION_COMPLETED':
      return { ...state, [action.inputName]: action.errorMessage ?? '' };
    case 'FORM_VALIDATION_COMPLETED':
      return action.errors;
    default:
      return state;
  }
}

type ErrorReducerDispatch = (action: ValidationAction) => void;

function scrollFirstInvalidInputIntoView(errors: Errors) {
  if (!errors) return;

  const inputNamesWithErrors = Object.keys(errors);
  if (inputNamesWithErrors.length === 0) return;

  const inputNamesQuery = inputNamesWithErrors
    .map(inputName => `[name='${inputName.toString()}']`)
    .join(', ');

  const firstErrorInDocumentOrder = document.querySelector(inputNamesQuery);
  if (!(firstErrorInDocumentOrder instanceof HTMLElement)) {
    // This is strange, we had an error but we don't have an <input> element for it?
    // So we can't really scroll to anywhere here, can we...
    return;
  }

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  firstErrorInDocumentOrder.scrollIntoView({
    behavior: prefersReducedMotion ? 'auto' : 'smooth'
  });

  firstErrorInDocumentOrder.focus({ preventScroll: true });
}

export function useErrorReducer(): [Errors, ErrorReducerDispatch] {
  return React.useReducer(errorReducer, {});
}

export function useFormValidations(): {
  errors: Errors;
  submitFormValidation: ReturnType<typeof createSubmitFormValidation>;
  blurInputValidation: ReturnType<typeof createBlurInputValidation>;
  validateInputField: ReturnType<typeof createValidateInputField>;
} {
  const [errors, dispatch] = useErrorReducer();

  return {
    errors,
    submitFormValidation: createSubmitFormValidation(dispatch),
    blurInputValidation: createBlurInputValidation(dispatch),
    validateInputField: createValidateInputField(dispatch)
  };
}

function createValidateInputField(
  dispatch: ErrorReducerDispatch
): (inputName: string, validations: Validations) => void {
  return (inputName, validations) =>
    validateInputField(inputName, validations, dispatch);
}

function createSubmitFormValidation(
  dispatch: ErrorReducerDispatch
): (evt: SyntheticEvent<Element>, validations: Validations) => void {
  return (evt, validations) => submitFormValidation(evt, validations, dispatch);
}

function submitFormValidation(
  evt: SyntheticEvent<Element>,
  validations: Validations,
  dispatch: ErrorReducerDispatch
) {
  const allErrors = runValidations(validations);

  if (Object.keys(allErrors).length > 0) {
    evt.preventDefault();
    scrollFirstInvalidInputIntoView(allErrors);
  }

  dispatch({
    type: 'FORM_VALIDATION_COMPLETED',
    errors: allErrors
  });
}

function createBlurInputValidation(
  dispatch: ErrorReducerDispatch
): (target: EventTarget, validations: Validations) => void {
  return (target, validations) =>
    blurInputValidation(target, validations, dispatch);
}

function blurInputValidation(
  target: EventTarget,
  validations: Validations,
  dispatch: ErrorReducerDispatch
) {
  if (
    (target instanceof HTMLInputElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLTextAreaElement) &&
    validations.get(target.name)
  ) {
    validateInputField(target.name, validations, dispatch);
  } else if (target instanceof HTMLDivElement) {
    // TODO: Fix how we are validating the time inputs
    // We are currently targing the div, because we can't
    // target the hidden input
    const name = target.dataset.name;
    if (typeof name === 'string' && validations.get(name)) {
      validateInputField(name, validations, dispatch);
    }
  }
}

function validateInputField(
  inputName: string,
  validations: Validations,
  dispatch: ErrorReducerDispatch
) {
  const errorMessage = runValidation(inputName, validations);
  dispatch({
    type: 'INPUT_VALIDATION_COMPLETED',
    inputName: inputName,
    errorMessage: errorMessage
  });
}
